/**
 * Outbound link, file download and contact click tracking.
 *
 * These are the conversions on a BID site. Nobody checks out here -- the
 * measurable intent is someone leaving for a member business's website or
 * social profile, pulling down the map & guide PDF, or tapping a phone number.
 * Pageviews don't capture any of it.
 *
 * One delegated listener on `document` handles the lot. That matters because
 * the places and real-estate directories render their cards client-side from
 * an EJS template (see locations-plp.js) and inject more on scroll, so any
 * handler bound to the links themselves would miss everything after the first
 * page. Delegation also means the listing templates need no tracking markup.
 *
 * Events are pushed to `dataLayer` for GTM to pick up and forward. Nothing here
 * talks to GA4 directly -- when collection moves to the server container the
 * transport changes and this file does not.
 *
 * Two notes for whoever configures the container:
 *
 * 1. GA4 Enhanced Measurement has its own "Outbound clicks" and "File
 *    downloads" toggles. Turn both OFF for this property or every click is
 *    counted twice. The events here replace them deliberately: they carry the
 *    business name and category, which Enhanced Measurement cannot see, and
 *    they survive the move to a first-party endpoint, which gtag's built-ins
 *    do not.
 *
 * 2. We do not delay navigation to "make sure" the beacon lands. Blocking a
 *    click behind a timeout is a UX regression and an unreliable one. GA4
 *    transports via `navigator.sendBeacon`, which is designed to survive
 *    unload; most links here are `target="_blank"` and don't unload the page
 *    at all.
 */

/** Extensions we count as a download rather than a page. */
const FILE_EXTENSIONS = /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|gz|tgz|tar|txt|rtf|dmg|pkg|mp3|mp4|mov|wav|avi)$/i;

/** Longest link label we'll record. Keeps the payload sane. */
const MAX_TEXT = 100;

/**
 * Readable label for a link. Falls back through the accessible name when the
 * link wraps an icon or an image rather than text.
 */
function linkText(link) {
    const text = (link.textContent || '').replace(/\s+/g, ' ').trim()
        || link.getAttribute('aria-label')
        || link.getAttribute('title')
        || (link.querySelector('img') || {}).alt
        || '';

    return text.slice(0, MAX_TEXT);
}

/**
 * Business or listing context for a click inside a directory card.
 *
 * Both the places and real-estate listings wrap each result in
 * `[data-js="location"]` and both put the name and the category badge in
 * predictable places, so one lookup serves both. Returns an empty object
 * anywhere else -- on an event page or the map & guide page the page itself is
 * the context, and GA4 attributes the event to it already.
 *
 * Named `listing_*` rather than `item_*` on purpose: `item_name`,
 * `item_category` and friends belong to GA4's ecommerce items schema. Flat
 * event parameters using those names collide with ecommerce reporting, which
 * matters because this pattern is meant to be lifted onto sites that do have
 * transactions.
 */
function cardContext(link) {
    const card = link.closest('[data-js="location"]');

    if (!card) {
        return {};
    }

    const name = card.querySelector('.title, .name');
    const category = card.querySelector('.category, .cat');
    const listing = card.closest('[data-post-type]');
    const context = {};

    if (name) {
        context.listing_name = name.textContent.replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT);
    }

    if (category) {
        context.listing_category = category.textContent.replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT);
    }

    if (listing) {
        context.listing_type = listing.getAttribute('data-post-type');
    }

    return context;
}

/**
 * Classify a link and build its event payload, or return null if it isn't
 * worth recording.
 *
 * Internal page links are deliberately ignored -- pageviews already cover
 * those, and counting them here would just inflate the event volume we're
 * about to start paying a server container for.
 */
function describeLink(link) {
    const href = link.getAttribute('href') || '';

    // Fragments, javascript: and empty hrefs never represent leaving.
    if (!href || href.charAt(0) === '#' || /^javascript:/i.test(href)) {
        return null;
    }

    // Neither the address nor the link text travels for these. A mailto: href
    // is a named person's work email -- personal data under GDPR, and PII that
    // Google's terms forbid sending to GA4 -- and on this site the link text is
    // the same address again. `contact_method` plus the page the click happened
    // on is the reportable fact and carries no personal data.
    //
    // If per-recipient reporting is ever wanted, label the link (a data
    // attribute naming the role, say) and send that. Never the address.
    if (link.protocol === 'mailto:') {
        return {
            event: 'contact_click',
            contact_method: 'email'
        };
    }

    if (link.protocol === 'tel:') {
        return {
            event: 'contact_click',
            contact_method: 'phone'
        };
    }

    // Everything past here should be a real web link.
    if (link.protocol !== 'http:' && link.protocol !== 'https:') {
        return null;
    }

    const extension = (link.pathname.match(FILE_EXTENSIONS) || [])[0];

    if (extension) {
        return {
            event: 'file_download',
            file_extension: extension.slice(1).toLowerCase(),
            file_name: link.pathname.split('/').pop(),
            link_url: link.href,
            link_text: linkText(link)
        };
    }

    // Hostname mismatch is the whole test. A link to another hostname is a
    // visitor leaving, which is exactly what the BID wants counted -- the
    // directory exists to send people to member businesses.
    if (link.hostname && link.hostname !== window.location.hostname) {
        return {
            event: 'outbound_click',
            link_domain: link.hostname,
            link_url: link.href,
            link_text: linkText(link)
        };
    }

    return null;
}

/**
 * Handle a click (or middle-click) anywhere in the document.
 */
function handleClick(event) {
    // auxclick covers every non-primary button; only the middle one opens a tab.
    if (event.type === 'auxclick' && event.button !== 1) {
        return;
    }

    const target = event.target;

    if (!target || typeof target.closest !== 'function') {
        return;
    }

    const link = target.closest('a[href]');

    if (!link) {
        return;
    }

    const payload = describeLink(link);

    if (!payload) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign(payload, cardContext(link)));
}

/**
 * Bind the delegated listeners. Safe to call once on ready; nothing here needs
 * rebinding when the directories inject more cards.
 */
function initOutboundTracking() {
    document.addEventListener('click', handleClick);
    document.addEventListener('auxclick', handleClick);
}

export { initOutboundTracking };
